// src/components/scenes/SettingsScene.js
// Filepath: // src/components/scenes/SettingsScene.js
/* eslint-disable no-console */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Box, Typography, Stack, Button, Divider } from "@mui/material";
import Focusable from "../common/Focusable";
import { useScene } from "../../state/SceneContext";
import { setSceneHandlers } from "../../input/remote";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../../firebase";
import {
  DEFAULT_TV_PREFS,
  fetchUserTvPrefs,
  saveUserTvPrefs,
} from "../../services/userPrefsService";

/** ---------- TV-FRIENDLY UI HELPERS ---------- */
function SettingGroup({ title, subtitle, children }) {
  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="h1" sx={{ fontWeight: 900, fontSize: 42, lineHeight: 1.1 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography sx={{ color: "text.secondary", mt: 1, fontSize: 22 }}>
          {subtitle}
        </Typography>
      )}
      <Box sx={{ mt: 3 }}>{children}</Box>
      <Divider sx={{ my: 4, opacity: 0.15 }} />
    </Box>
  );
}

function ToggleButton({ on, label, onClick, minWidth = 520 }) {
  return (
    <Button
      variant={on ? "contained" : "outlined"}
      onClick={onClick}
      sx={{
        minWidth,
        height: 82,
        px: 4,
        fontWeight: 900,
        typography: "h2",
        borderRadius: "999px", // pill shape
      }}
    >
      {label}: <strong style={{ marginLeft: 14 }}>{on ? "On" : "Off"}</strong>
    </Button>
  );
}

/** ---------- SCENE ---------- */
export default function SettingsScene() {
  const { setScene } = useScene();

  // Defaults (simplified): only the settings we keep
  const defaults = useMemo(() => ({ ...DEFAULT_TV_PREFS }), []);

  const [uid, setUid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Local state for editable prefs
  const [s, setS] = useState(defaults);

  // Auth -> load remote prefs or fall back to defaults
  useEffect(() => {
    const stop = onAuthStateChanged(auth, async (user) => {
      setUid(user ? user.uid : null);
      try {
        if (user) {
          const fetched = await fetchUserTvPrefs(user.uid);
          setS(fetched);
        } else {
          setS(defaults);
        }
      } catch (err) {
        console.error("[Settings] Failed to load prefs:", err);
        setS(defaults);
      } finally {
        setLoading(false);
      }
    });
    return () => stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveIfDirty = useCallback(
    async (nextSceneName = "home") => {
      if (!uid || !dirty) {
        setScene({ name: nextSceneName });
        return;
      }
      try {
        setSaving(true);
        await saveUserTvPrefs(uid, s);
      } catch (err) {
        console.error("[Settings] Failed to save prefs:", err);
      } finally {
        setSaving(false);
        setDirty(false);
        setScene({ name: nextSceneName });
      }
    },
    [uid, dirty, s, setScene]
  );

  // Back/Esc/Remote Back -> Save (if changed) then Home
  useEffect(() => {
    setSceneHandlers({
      onBack: () => {
        saveIfDirty("home");
        return true;
      },
    });
    return () => setSceneHandlers({});
  }, [saveIfDirty]);

  const toggle = (key) => {
    setS((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      return next;
    });
    setDirty(true);
  };
  const step = (key, delta, { min = 1, max = 60 } = {}) => {
    setS((prev) => {
      const nextVal = Math.max(min, Math.min(max, (prev[key] ?? 1) + delta));
      const next = { ...prev, [key]: nextVal };
      return next;
    });
    setDirty(true);
  };
  const resetAll = () => {
    setS(defaults);
    setDirty(true);
  };

  if (loading) {
    return (
      <Box
        className="scene"
        sx={{
          height: "100%",
          display: "grid",
          placeItems: "center",
          p: 4,
        }}
        data-rails-root
      >
        <Typography variant="h1" sx={{ fontSize: 56, fontWeight: 900 }}>
          Loading Settings…
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      className="scene"
      sx={{
        height: "100%",
        display: "grid",
        gridTemplateRows: "auto 1fr auto",
        gap: 5,            // more spacing between major areas
        p: 5,              // bigger padding for leanback
        maxWidth: 1720,
        mx: "auto",
      }}
      data-rails-root
    >
      {/* Header Row: Title + Done (big, rounded) */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h1" sx={{ fontWeight: 900, fontSize: 64, lineHeight: 1 }}>
          Settings
        </Typography>

        <Focusable row={90} col={0} onClick={() => saveIfDirty("home")}>
          <Button
            variant="contained"
            sx={{
              height: 82,
              minWidth: 320,
              px: 5,
              fontWeight: 900,
              typography: "h2",
              borderRadius: "999px",
            }}
            disabled={saving}
          >
            {saving ? "Saving…" : "Done"}
          </Button>
        </Focusable>
      </Box>

      {/* Content */}
      <Box sx={{ display: "grid", gap: 5 }}>
        {/* =================== Gallery =================== */}
        <SettingGroup title="Gallery" subtitle="Image gallery behavior">
          <Stack direction="row" spacing={3} flexWrap="wrap" rowGap={3} sx={{ mb: 2 }}>
            <Focusable row={300} col={0} autoFocus onClick={() => toggle("galleryRandom")}>
              <ToggleButton on={s.galleryRandom} label="Random Order" />
            </Focusable>
          </Stack>

          <Stack
            direction="row"
            spacing={3}
            alignItems="center"
            flexWrap="wrap"
            rowGap={3}
          >
            <Typography variant="h2" sx={{ mr: 2, fontWeight: 900, fontSize: 36 }}>
              Time per Image
            </Typography>

            <Focusable row={311} col={0} onClick={() => step("galleryMinutes", -1)}>
              <Button
                variant="outlined"
                sx={{
                  minWidth: 96,
                  height: 82,
                  fontWeight: 900,
                  typography: "h2",
                  borderRadius: "999px",
                }}
              >
                –
              </Button>
            </Focusable>

            <Box
              sx={{
                px: 4,
                py: 2,
                borderRadius: "999px",
                bgcolor: "background.default",
                minWidth: 220,
                textAlign: "center",
              }}
            >
              <Typography variant="h1" sx={{ fontWeight: 900, fontSize: 40 }}>
                {s.galleryMinutes} min
              </Typography>
            </Box>

            <Focusable row={311} col={1} onClick={() => step("galleryMinutes", +1)}>
              <Button
                variant="outlined"
                sx={{
                  minWidth: 96,
                  height: 82,
                  fontWeight: 900,
                  typography: "h2",
                  borderRadius: "999px",
                }}
              >
                +
              </Button>
            </Focusable>

            <Typography sx={{ color: "text.secondary", fontSize: 22, ml: 2 }}>
              (1–60 minutes)
            </Typography>
          </Stack>
        </SettingGroup>

        {/* =================== Video =================== */}
        <SettingGroup title="Video" subtitle="Playback behavior">
          <Stack direction="row" spacing={3} flexWrap="wrap" rowGap={3}>
            <Focusable row={420} col={0} onClick={() => toggle("loopVideos")}>
              <ToggleButton on={s.loopVideos} label="Loop Video" />
            </Focusable>
          </Stack>

          <Typography sx={{ color: "text.secondary", mt: 2, fontSize: 22 }}>
            When On, videos repeat continuously. When Off, videos play once.
          </Typography>
        </SettingGroup>
      </Box>

      {/* Footer actions */}
      <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
        <Focusable row={900} col={0} onClick={resetAll}>
          <Button
            variant="outlined"
            sx={{
              fontWeight: 900,
              height: 82,
              minWidth: 320,
              borderRadius: "999px",
              typography: "h2",
            }}
          >
            Reset to Defaults
          </Button>
        </Focusable>
      </Box>
    </Box>
  );
}
